import {
  LoadStopsItem,
  LoadStopsItemTypes,
  NormalizedDropoff,
  NormalizedLoad,
  NormalizedPickup,
  createInitLoad,
  normalizeLoad,
} from 'types/Load';
import { Maybe, MaybeUndef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import {
  parseFormDate,
  validateDeliveryOnOrAfterPickupDate,
} from '../utils/stopValidation';
import { PLS_APPOINTMENT_TYPE } from './constants';

function plsComparableTime(value: unknown): string {
  if (value == null || value === '') {
    return '';
  }

  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value).trim();
  }

  return String(date.getTime());
}

// PLS has no appointment-type field. By appointment is a point-in-time window.
export function resolvePlsAppointmentType(
  apptType: unknown,
  earliest: unknown,
  latest: unknown
): string {
  const existing = String(apptType ?? '').trim();
  if (existing) {
    return existing;
  }

  const earliestTime = plsComparableTime(earliest);
  const latestTime = plsComparableTime(latest);
  if (earliestTime !== '' && earliestTime === latestTime) {
    return PLS_APPOINTMENT_TYPE.ByAppointment;
  }

  return PLS_APPOINTMENT_TYPE.FCFS;
}

function hasPlsStopTime(value: unknown): boolean {
  return value != null && String(value).trim() !== '';
}

function plsTimesMatch(left: unknown, right: unknown): boolean {
  const leftTime = plsComparableTime(left);
  const rightTime = plsComparableTime(right);
  return leftTime !== '' && leftTime === rightTime;
}

// A leftover Kit appointment end is only a latest window when it is the same
// instant as earliest. Anything else can be earlier than the stop and fail submit.
// Return the original value. Hydrated PLS times are Dates shifted for wall-clock
// display, and Date#toString() would rewrite them in the browser timezone.
function plsRouteLatest(
  earliest: unknown,
  latest: unknown,
  leftoverEnd: unknown
): string {
  if (hasPlsStopTime(latest)) {
    return latest as string;
  }
  if (plsTimesMatch(earliest, leftoverEnd)) {
    return leftoverEnd as string;
  }
  return '';
}

function toDateString(value: unknown): string {
  if (!value) {
    return '';
  }

  if (value instanceof Date) {
    return value.toISOString();
  }

  return String(value);
}

function toMaybeDate(value: unknown): Maybe<Date> {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    return value;
  }

  const parsed = new Date(String(value));
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function defaultPlsPickupConsignee(): Pick<
  NormalizedLoad,
  'pickup' | 'consignee'
> {
  const base = normalizeLoad(TMS.PLS, createInitLoad());

  return {
    pickup: base.pickup,
    consignee: base.consignee,
  };
}

/** Origin + destination + up to 10 PLS stopoffs (CreateLoadRequest.stopoffs maxItems: 10). */
export const MAX_PLS_ROUTE_STOPS = 12;

export function buildPlsStopsFromPickupAndConsignee(
  pickup: MaybeUndef<NormalizedPickup>,
  consignee: MaybeUndef<NormalizedDropoff>
): LoadStopsItem[] {
  const defaultStops = createInitLoad().stops!;

  return [
    buildPlsRouteStopFromFlatLocation(
      0,
      pickup,
      LoadStopsItemTypes.Pickup,
      defaultStops[0]
    ),
    buildPlsRouteStopFromFlatLocation(
      1,
      consignee,
      LoadStopsItemTypes.Dropoff,
      defaultStops[1]
    ),
  ];
}

function buildPlsRouteStopFromFlatLocation(
  index: number,
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>,
  stopType: LoadStopsItemTypes,
  fallback: LoadStopsItem
): LoadStopsItem {
  if (!location) {
    return {
      ...fallback,
      order: index,
      stopNumber: index,
      stopType,
    };
  }

  const isDropoff = stopType === LoadStopsItemTypes.Dropoff;
  const readyTime = toDateString(
    (location as NormalizedPickup).readyTime ?? location.apptStartTime
  );
  const readyEndTime = toDateString(
    isDropoff
      ? ((location as NormalizedDropoff).mustDeliverEnd ?? location.apptEndTime)
      : ((location as NormalizedPickup).readyTimeEnd ?? location.apptEndTime)
  );
  const mustDeliver = toDateString(
    (location as NormalizedDropoff).mustDeliver ?? location.apptStartTime
  );
  const apptType = resolvePlsAppointmentType(
    location.apptType,
    isDropoff ? mustDeliver : readyTime,
    readyEndTime
  );

  return {
    ...fallback,
    order: index,
    stopNumber: index,
    stopType,
    externalTMSID: location.externalTMSID ?? '',
    contact: location.contact ?? '',
    phone: location.phone ?? '',
    email: location.email ?? '',
    refNumber: location.refNumber ?? '',
    apptRequired: location.apptRequired ?? false,
    apptType,
    apptStartTime: '',
    apptEndTime: '',
    apptNote: location.apptNote ?? '',
    timezone: location.timezone ?? '',
    additionalReferences: location.additionalReferences ?? [],
    readyTime,
    readyEndTime,
    mustDeliver,
    address: {
      name: location.name ?? '',
      addressLine1: location.addressLine1 ?? '',
      addressLine2: location.addressLine2 ?? '',
      city: location.city ?? '',
      state: location.state ?? '',
      zip: location.zipCode ?? '',
      country: location.country ?? '',
      timezone: location.timezone ?? '',
      isVerified: false,
    },
  };
}

export function isPlsPickupStopType(stopType: string | undefined): boolean {
  const normalized = String(stopType ?? '')
    .trim()
    .toLowerCase();
  // Legacy generic "stop" treated as pickup for older drafts / payloads.
  return (
    normalized === LoadStopsItemTypes.Pickup ||
    normalized === LoadStopsItemTypes.Stop
  );
}

export function isPlsDropoffStopType(stopType: string | undefined): boolean {
  const normalized = String(stopType ?? '')
    .trim()
    .toLowerCase();
  return (
    normalized === LoadStopsItemTypes.Dropoff ||
    normalized === LoadStopsItemTypes.Delivery
  );
}

export function createPlsIntermediateStop(order: number): LoadStopsItem {
  const template = createInitLoad().stops![1];

  return {
    ...template,
    order,
    stopNumber: order,
    stopType: LoadStopsItemTypes.Pickup,
    refNumber: '',
    readyTime: '',
    readyEndTime: '',
    mustDeliver: '',
    apptType: PLS_APPOINTMENT_TYPE.FCFS,
    additionalReferences: [],
  };
}

export function normalizePlsRouteStopTypes(
  stops: LoadStopsItem[]
): LoadStopsItem[] {
  if (stops.length === 0) {
    return stops;
  }

  return stops.map((stop, index) => {
    let stopType: LoadStopsItemTypes;
    if (index === 0) {
      stopType = LoadStopsItemTypes.Pickup;
    } else if (index === stops.length - 1) {
      stopType = LoadStopsItemTypes.Dropoff;
    } else if (isPlsDropoffStopType(stop.stopType)) {
      stopType = LoadStopsItemTypes.Dropoff;
    } else {
      stopType = LoadStopsItemTypes.Pickup;
    }

    const isDropoff = stopType === LoadStopsItemTypes.Dropoff;
    const earliest = isDropoff ? stop.mustDeliver : stop.readyTime;
    const latest = plsRouteLatest(
      earliest,
      stop.readyEndTime,
      stop.apptEndTime
    );

    return {
      ...stop,
      order: index,
      stopNumber: index,
      stopType,
      readyEndTime: latest,
      // Drop the leftover so a later submit cannot refill a cleared latest.
      apptEndTime: '',
      apptType: resolvePlsAppointmentType(stop.apptType, earliest, latest),
    };
  });
}

export function syncPlsRouteStopsToPickupConsignee(
  stops: LoadStopsItem[]
): Pick<NormalizedLoad, 'pickup' | 'consignee'> {
  const defaults = defaultPlsPickupConsignee();

  if (stops.length < 2) {
    return defaults;
  }

  const firstStop = stops[0];
  const lastStop = stops[stops.length - 1];

  return {
    pickup: flatPickupFromRouteStop(firstStop, defaults.pickup),
    consignee: flatDropoffFromRouteStop(lastStop, defaults.consignee),
  };
}

function flatPickupFromRouteStop(
  stop: LoadStopsItem,
  base: NormalizedPickup
): NormalizedPickup {
  return {
    ...base,
    externalTMSID: stop.externalTMSID ?? '',
    name: stop.address?.name ?? '',
    addressLine1: stop.address?.addressLine1 ?? '',
    addressLine2: stop.address?.addressLine2 ?? '',
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zipCode: stop.address?.zip ?? '',
    country: stop.address?.country ?? '',
    contact: stop.contact ?? '',
    phone: stop.phone ?? '',
    email: stop.email ?? '',
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptType: resolvePlsAppointmentType(
      stop.apptType,
      stop.readyTime,
      plsRouteLatest(stop.readyTime, stop.readyEndTime, stop.apptEndTime)
    ),
    apptStartTime: null,
    apptEndTime: null,
    apptNote: stop.apptNote ?? '',
    timezone: stop.timezone ?? stop.address?.timezone ?? '',
    additionalReferences: stop.additionalReferences ?? [],
    readyTime: toMaybeDate(stop.readyTime),
    readyTimeEnd: toMaybeDate(
      plsRouteLatest(stop.readyTime, stop.readyEndTime, stop.apptEndTime)
    ),
  };
}

function flatDropoffFromRouteStop(
  stop: LoadStopsItem,
  base: NormalizedDropoff
): NormalizedDropoff {
  return {
    ...base,
    externalTMSID: stop.externalTMSID ?? '',
    name: stop.address?.name ?? '',
    addressLine1: stop.address?.addressLine1 ?? '',
    addressLine2: stop.address?.addressLine2 ?? '',
    city: stop.address?.city ?? '',
    state: stop.address?.state ?? '',
    zipCode: stop.address?.zip ?? '',
    country: stop.address?.country ?? '',
    contact: stop.contact ?? '',
    phone: stop.phone ?? '',
    email: stop.email ?? '',
    refNumber: stop.refNumber ?? '',
    apptRequired: stop.apptRequired ?? false,
    apptType: resolvePlsAppointmentType(
      stop.apptType,
      stop.mustDeliver,
      plsRouteLatest(stop.mustDeliver, stop.readyEndTime, stop.apptEndTime)
    ),
    apptStartTime: null,
    apptEndTime: null,
    apptNote: stop.apptNote ?? '',
    timezone: stop.timezone ?? stop.address?.timezone ?? '',
    additionalReferences: stop.additionalReferences ?? [],
    mustDeliver: toMaybeDate(stop.mustDeliver),
    mustDeliverEnd: toMaybeDate(
      plsRouteLatest(stop.mustDeliver, stop.readyEndTime, stop.apptEndTime)
    ),
  };
}

export function isPlsMultiStopRoute(
  stops: MaybeUndef<LoadStopsItem[]>
): boolean {
  return (stops?.length ?? 0) > 2;
}

export function plsStopHasRouteContent(
  stop: MaybeUndef<LoadStopsItem>
): boolean {
  if (!stop) {
    return false;
  }

  return Boolean(
    stop.externalTMSID?.trim() ||
    stop.refNumber?.trim() ||
    stop.address?.name?.trim() ||
    stop.address?.city?.trim() ||
    stop.address?.addressLine1?.trim() ||
    String(stop.readyTime ?? '').trim() ||
    String(stop.mustDeliver ?? '').trim()
  );
}

export function plsFlatLocationHasRouteContent(
  location: MaybeUndef<NormalizedPickup | NormalizedDropoff>
): boolean {
  if (!location) {
    return false;
  }

  return Boolean(
    location.externalTMSID?.trim() ||
    location.refNumber?.trim() ||
    location.name?.trim() ||
    location.city?.trim() ||
    location.addressLine1?.trim() ||
    (location as NormalizedPickup).readyTime ||
    (location as NormalizedDropoff).mustDeliver
  );
}

export function plsRouteEndsHaveContent(stops: LoadStopsItem[]): boolean {
  if (stops.length < 2) {
    return false;
  }

  return (
    plsStopHasRouteContent(stops[0]) ||
    plsStopHasRouteContent(stops[stops.length - 1])
  );
}

export function plsRouteStopLabel(
  stops: LoadStopsItem[],
  index: number
): string {
  if (index <= 0) {
    return 'Pickup';
  }

  if (index >= stops.length - 1) {
    return 'Consignee';
  }

  const action = isPlsDropoffStopType(stops[index]?.stopType)
    ? 'Dropoff'
    : 'Pickup';
  return `Stop ${index} · ${action}`;
}

export function plsRouteStopCollapsedSummary(
  stop: MaybeUndef<LoadStopsItem>
): string {
  if (!stop) {
    return '';
  }

  const name = stop.address?.name?.trim() ?? '';
  const city = stop.address?.city?.trim() ?? '';
  const state = stop.address?.state?.trim() ?? '';
  const place = [city, state].filter(Boolean).join(', ');

  if (name && place) {
    return `${name} · ${place}`;
  }

  return name || place;
}

function routeStopComparableTime(stop: LoadStopsItem): unknown {
  if (isPlsDropoffStopType(stop.stopType)) {
    const dropLatest = parseFormDate(stop.readyEndTime);
    return dropLatest ? stop.readyEndTime : stop.mustDeliver;
  }

  const readyEnd = parseFormDate(stop.readyEndTime);
  return readyEnd ? stop.readyEndTime : stop.readyTime;
}

export function validatePlsMonotonicRouteStopTimes(
  stops: LoadStopsItem[]
): { ok: true } | { ok: false; message: string; pathIndex: number } {
  for (let i = 0; i < stops.length - 1; i++) {
    const currentStop = stops[i];
    const nextStop = stops[i + 1];
    const currentTime = routeStopComparableTime(currentStop);
    const nextTime = isPlsDropoffStopType(nextStop.stopType)
      ? nextStop.mustDeliver
      : nextStop.readyTime;

    const result = validateDeliveryOnOrAfterPickupDate(currentTime, nextTime, {
      compareTime: true,
    });

    if (!result.ok) {
      return {
        ok: false,
        message: result.message,
        pathIndex: i + 1,
      };
    }
  }

  return { ok: true };
}

export function ensurePlsRouteStops(values: {
  stops?: MaybeUndef<LoadStopsItem[]>;
  pickup?: MaybeUndef<NormalizedPickup>;
  consignee?: MaybeUndef<NormalizedDropoff>;
}): LoadStopsItem[] {
  const existing = values.stops ?? [];
  const hasIntermediateContent = existing
    .slice(1, -1)
    .some((stop) => plsStopHasRouteContent(stop));

  // Keep an existing multi-stop route (or any route with end/middle content)
  // before rebuilding from flat pickup/consignee, which would drop middles.
  if (
    existing.length > 2 ||
    (existing.length >= 2 &&
      (plsRouteEndsHaveContent(existing) || hasIntermediateContent))
  ) {
    return normalizePlsRouteStopTypes(existing);
  }

  if (
    plsFlatLocationHasRouteContent(values.pickup) ||
    plsFlatLocationHasRouteContent(values.consignee)
  ) {
    return buildPlsStopsFromPickupAndConsignee(values.pickup, values.consignee);
  }

  if (existing.length >= 2) {
    return normalizePlsRouteStopTypes(existing);
  }

  return buildPlsStopsFromPickupAndConsignee(values.pickup, values.consignee);
}
